import { useState, useCallback, useMemo } from 'react';

/**
 * Custom hook for handling sorting of data
 * @param {array} items - Array of items to sort
 * @param {string} initialSortField - Initial field to sort by
 * @param {string} initialSortDirection - Initial sort direction ('asc' or 'desc')
 * @returns {object} Sorting state and handlers
 */
const useSorting = (
  items = [],
  initialSortField = null,
  initialSortDirection = 'asc'
) => {
  const [sortField, setSortField] = useState(initialSortField);
  const [sortDirection, setSortDirection] = useState(initialSortDirection);
  
  // Handle sort change
  const handleSortChange = useCallback((field) => {
    // If clicking the same field, toggle direction
    if (field === sortField) {
      setSortDirection(prevDirection => prevDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new field, set it as the sort field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField]);
  
  // Reset sorting to initial values
  const resetSorting = useCallback(() => {
    setSortField(initialSortField);
    setSortDirection(initialSortDirection);
  }, [initialSortField, initialSortDirection]);
  
  // Get sorted items
  const sortedItems = useMemo(() => {
    // If no sort field is specified, return the original items
    if (!sortField) return items;
    
    // Create a copy of the items array to avoid mutating the original
    return [...items].sort((a, b) => {
      // Handle nested properties (e.g., 'user.name')
      const getNestedValue = (obj, path) => {
        return path.split('.').reduce((o, key) => (o && o[key] !== undefined) ? o[key] : null, obj);
      };
      
      const aValue = getNestedValue(a, sortField);
      const bValue = getNestedValue(b, sortField);
      
      // Handle different data types
      if (aValue === null || aValue === undefined) return sortDirection === 'asc' ? -1 : 1;
      if (bValue === null || bValue === undefined) return sortDirection === 'asc' ? 1 : -1;
      
      // Compare dates
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortDirection === 'asc' 
          ? aValue.getTime() - bValue.getTime() 
          : bValue.getTime() - aValue.getTime();
      }
      
      // Try to parse strings as dates
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        
        if (!isNaN(aDate) && !isNaN(bDate)) {
          return sortDirection === 'asc' 
            ? aDate.getTime() - bDate.getTime() 
            : bDate.getTime() - aDate.getTime();
        }
      }
      
      // Compare numbers
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // Compare strings (case-insensitive)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue, undefined, { sensitivity: 'base' }) 
          : bValue.localeCompare(aValue, undefined, { sensitivity: 'base' });
      }
      
      // Compare booleans
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return sortDirection === 'asc' 
          ? (aValue === bValue ? 0 : aValue ? 1 : -1) 
          : (aValue === bValue ? 0 : aValue ? -1 : 1);
      }
      
      // Fallback for other types
      return 0;
    });
  }, [items, sortField, sortDirection]);
  
  // Get sort info for UI
  const sortInfo = useMemo(() => {
    return {
      field: sortField,
      direction: sortDirection,
      isAscending: sortDirection === 'asc',
      isDescending: sortDirection === 'desc',
    };
  }, [sortField, sortDirection]);
  
  return {
    sortedItems,
    sortInfo,
    handleSortChange,
    resetSorting,
  };
};

export default useSorting;
